import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import {
  IconAlertCircle,
  IconBox,
  IconCheck,
  IconCrown,
  IconDownload,
  IconSearch,
} from '@tabler/icons-react';
import PropTypes from 'prop-types';
import { collection, onSnapshot, orderBy, query, serverTimestamp, updateDoc, where, doc } from 'firebase/firestore';
import { notifications } from '@mantine/notifications';
import * as XLSX from 'xlsx';
import { db } from '../services/firebase';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../contexts/AuthContext';

const STATUS = {
  pending: { label: 'Pendiente', color: 'yellow' },
  active: { label: 'Activa', color: 'green' },
  past_due: { label: 'Pago atrasado', color: 'orange' },
  payment_rejected: { label: 'Pago rechazado', color: 'red' },
  paused: { label: 'Pausada', color: 'gray' },
  cancelled: { label: 'Cancelada', color: 'red' },
  expired: { label: 'Vencida', color: 'dark' },
  incomplete: { label: 'Incompleta', color: 'yellow' },
  error: { label: 'Error de sincronización', color: 'red' },
};

const GIFT = {
  eyebrows: 'Cejas con navaja',
  facial_mask: 'Mascarilla facial',
};

const BRANCH = {
  bellas_artes: 'Bellas Artes',
  providencia: 'Providencia',
  nunoa: 'Ñuñoa',
};

const toDate = (value) => value?.toDate?.() || (value ? new Date(value) : null);
const formatDate = (value, withTime = false) => {
  const date = toDate(value);
  if (!date || Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('es-CL', {
    dateStyle: 'medium',
    ...(withTime ? { timeStyle: 'short' } : {}),
  }).format(date);
};
const formatMoney = (value) => new Intl.NumberFormat('es-CL', {
  style: 'currency', currency: 'CLP', maximumFractionDigits: 0,
}).format(Number(value || 0));
const normalizeSearch = (value = '') => String(value).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9k+@.-]/g, '');
const getBoxProgress = (item) => Math.max(0, Number(item?.boxProgress ?? (Number(item?.approvedPaymentCount || 0) % 3)));
const getEstimatedBoxDate = (item) => {
  const nextPayment = toDate(item?.nextPaymentAt);
  if (!nextPayment) return 'Por confirmar';
  const pendingPayments = 3 - getBoxProgress(item);
  const estimate = new Date(nextPayment);
  estimate.setMonth(estimate.getMonth() + Math.max(0, pendingPayments - 1));
  return formatDate(estimate);
};

const StatusBadge = ({ status }) => {
  const config = STATUS[status] || STATUS.incomplete;
  return <Badge color={config.color} variant="light" size="sm">{config.label}</Badge>;
};
StatusBadge.propTypes = { status: PropTypes.string };

const DataLine = ({ label, value }) => (
  <Box>
    <Text size="xs" c="dimmed" tt="uppercase" fw={700}>{label}</Text>
    <Text size="sm" fw={600} mt={3} style={{ overflowWrap: 'anywhere' }}>{value || '—'}</Text>
  </Box>
);
DataLine.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.node };

export default function PremiumClub() {
  const { currentUser } = useAuth();
  const isMobile = useMediaQuery('(max-width: 48em)');
  const [subscriptions, setSubscriptions] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [boxRewards, setBoxRewards] = useState([]);
  const [boxesLoading, setBoxesLoading] = useState(false);
  const [deliveringBoxId, setDeliveringBoxId] = useState('');
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState('created_desc');
  const [selected, setSelected] = useState(null);
  const [opened, { open, close }] = useDisclosure(false);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'premiumClubSubscriptions'),
      (snapshot) => {
        setSubscriptions(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        setLoading(false);
      },
      (caught) => {
        console.error('Error cargando Premium Club:', caught);
        setError('No se pudieron cargar las membresías. Revisa los permisos de Firestore.');
        setLoading(false);
      },
    );
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!selected?.id) { setEvents([]); return undefined; }
    setEventsLoading(true);
    const eventsQuery = query(
      collection(db, 'premiumClubEvents'),
      where('subscriptionId', '==', selected.id),
      orderBy('receivedAt', 'desc'),
    );
    const unsubscribe = onSnapshot(eventsQuery, (snapshot) => {
      setEvents(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
      setEventsLoading(false);
    }, (caught) => {
      console.error('Error cargando eventos Premium Club:', caught);
      setEvents([]);
      setEventsLoading(false);
    });
    return unsubscribe;
  }, [selected?.id]);

  useEffect(() => {
    setBoxesLoading(true);
    const unsubscribe = onSnapshot(collection(db, 'premiumClubBoxRewards'), (snapshot) => {
      setBoxRewards(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))
        .sort((a, b) => Number(b.cycleNumber || 0) - Number(a.cycleNumber || 0)));
      setBoxesLoading(false);
    }, (caught) => {
      console.error('Error cargando boxes Premium Club:', caught);
      setBoxRewards([]);
      setBoxesLoading(false);
    });
    return unsubscribe;
  }, []);

  const filtered = useMemo(() => {
    const needle = normalizeSearch(search);
    return subscriptions
      .filter((item) => status === 'all' || item.status === status)
      .filter((item) => !needle || [item.fullName, item.rut, item.email, item.phone]
        .some((value) => normalizeSearch(value).includes(needle)))
      .sort((a, b) => {
        const field = sort.startsWith('activated') ? 'activatedAt' : 'createdAt';
        const direction = sort.endsWith('asc') ? 1 : -1;
        return ((toDate(a[field])?.getTime() || 0) - (toDate(b[field])?.getTime() || 0)) * direction;
      });
  }, [search, sort, status, subscriptions]);

  const totals = useMemo(() => ({
    all: subscriptions.length,
    active: subscriptions.filter((item) => item.status === 'active').length,
    attention: subscriptions.filter((item) => ['past_due', 'payment_rejected', 'error'].includes(item.status)).length,
    boxesPending: boxRewards.filter((reward) => reward.status === 'eligible').length,
  }), [boxRewards, subscriptions]);

  const selectedBoxRewards = useMemo(
    () => boxRewards.filter((reward) => reward.subscriptionId === selected?.id),
    [boxRewards, selected?.id],
  );

  const showDetails = (item) => { setSelected(item); open(); };
  const closeDetails = () => { close(); window.setTimeout(() => setSelected(null), 180); };

  const markBoxDelivered = async (reward) => {
    if (!currentUser?.uid || !currentUser?.email || reward.status !== 'eligible') return;
    const branch = selected?.preferredPickupBranch;
    if (!BRANCH[branch]) {
      notifications.show({ color: 'red', title: 'Sucursal pendiente', message: 'La membresía no tiene una sucursal de retiro válida.' });
      return;
    }
    if (!window.confirm(`¿Confirmas la entrega del box ${reward.cycleNumber} en ${BRANCH[branch]}?`)) return;
    setDeliveringBoxId(reward.id);
    try {
      await updateDoc(doc(db, 'premiumClubBoxRewards', reward.id), {
        status: 'delivered',
        deliveredAt: serverTimestamp(),
        deliveredBranch: branch,
        deliveredByUid: currentUser.uid,
        deliveredByEmail: currentUser.email,
        updatedAt: serverTimestamp(),
      });
      notifications.show({ color: 'green', title: 'Box entregado', message: 'La entrega quedó registrada con responsable y sucursal.' });
    } catch (caught) {
      console.error('Error registrando entrega de box:', caught);
      notifications.show({ color: 'red', title: 'No se pudo registrar', message: 'Revisa tu conexión o los permisos e inténtalo nuevamente.' });
    } finally {
      setDeliveringBoxId('');
    }
  };

  const exportExcel = () => {
    const rows = filtered.map((item) => ({
      Nombre: item.fullName || `${item.firstName || ''} ${item.lastName || ''}`.trim(),
      RUT: item.rut || '', Correo: item.email || '', Teléfono: item.phone || '',
      Estado: STATUS[item.status]?.label || item.status, 'Fecha de nacimiento': item.birthDate || '',
      'Regalo de bienvenida': GIFT[item.welcomeGift] || item.welcomeGift || '',
      'Sucursal de retiro': BRANCH[item.preferredPickupBranch] || item.preferredPickupBranch || '',
      'Pagos aprobados': Number(item.approvedPaymentCount || 0),
      'Avance box': `${getBoxProgress(item)} de 3`,
      'Boxes obtenidos': Number(item.boxRewardsEarned || 0),
      'Boxes entregados': boxRewards.filter((reward) => reward.subscriptionId === item.id && reward.status === 'delivered').length,
      'Próximo box estimado': getEstimatedBoxDate(item),
      'Fecha de activación': formatDate(item.activatedAt), 'Último pago': formatDate(item.lastPaymentAt),
      'Próximo cobro': formatDate(item.nextPaymentAt), Monto: Number(item.amount || 0),
      'ID Mercado Pago': item.externalSubscriptionId || '', Actualización: formatDate(item.updatedAt, true),
    }));
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet['!cols'] = [{ wch: 28 }, { wch: 16 }, { wch: 30 }, { wch: 18 }, { wch: 20 }, { wch: 18 }, { wch: 24 }, { wch: 20 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 18 }, { wch: 22 }, { wch: 20 }, { wch: 20 }, { wch: 12 }, { wch: 30 }, { wch: 22 }];
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Premium Club');
    XLSX.writeFile(workbook, `premium-club-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <Container size="xl" py={{ base: 'md', sm: 'xl' }}>
      <PageHeader
        title="Premium Club"
        description="Consulta suscriptores, pagos y cambios de estado confirmados por Mercado Pago."
        icon={IconCrown}
      />

      {error && <Alert icon={<IconAlertCircle size={18} />} color="red" mb="lg">{error}</Alert>}

      <SimpleGrid cols={{ base: 1, sm: 4 }} spacing="sm" mb="lg">
        <Paper withBorder p="md" radius="lg"><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Suscripciones</Text><Title order={2} mt={4}>{totals.all}</Title></Paper>
        <Paper withBorder p="md" radius="lg"><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Activas</Text><Title order={2} mt={4} c="green.7">{totals.active}</Title></Paper>
        <Paper withBorder p="md" radius="lg"><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Requieren atención</Text><Title order={2} mt={4} c={totals.attention ? 'red.7' : 'gray.7'}>{totals.attention}</Title></Paper>
        <Paper withBorder p="md" radius="lg"><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Boxes por entregar</Text><Title order={2} mt={4} c="red.7">{totals.boxesPending}</Title></Paper>
      </SimpleGrid>

      <Paper withBorder radius="lg" p={{ base: 'sm', sm: 'md' }}>
        <Group justify="space-between" align="end" gap="sm" mb="md">
          <Group align="end" gap="sm" style={{ flex: 1 }}>
            <TextInput leftSection={<IconSearch size={16} />} label="Buscar" placeholder="Nombre, RUT, correo o teléfono" value={search} onChange={(event) => setSearch(event.currentTarget.value)} style={{ flex: 1, minWidth: 220 }} />
            <Select label="Estado" value={status} onChange={(value) => setStatus(value || 'all')} data={[{ value: 'all', label: 'Todos' }, ...Object.entries(STATUS).map(([value, item]) => ({ value, label: item.label }))]} w={190} />
            <Select label="Orden" value={sort} onChange={(value) => setSort(value || 'created_desc')} data={[{ value: 'created_desc', label: 'Más recientes' }, { value: 'created_asc', label: 'Más antiguas' }, { value: 'activated_desc', label: 'Activación reciente' }, { value: 'activated_asc', label: 'Activación antigua' }]} w={190} />
          </Group>
          <Button variant="light" leftSection={<IconDownload size={17} />} onClick={exportExcel} disabled={!filtered.length}>Exportar</Button>
        </Group>

        {loading ? <Group justify="center" py="xl"><Loader /></Group> : filtered.length === 0 ? (
          <Text ta="center" c="dimmed" py="xl">No hay suscripciones para los filtros seleccionados.</Text>
        ) : isMobile ? (
          <Stack gap="sm">
            {filtered.map((item) => (
              <Paper key={item.id} withBorder p="md" radius="md" onClick={() => showDetails(item)} style={{ cursor: 'pointer' }}>
                <Group justify="space-between" align="start" wrap="nowrap"><Box><Text fw={700}>{item.fullName}</Text><Text size="sm" c="dimmed">{item.rut}</Text></Box><StatusBadge status={item.status} /></Group>
                <Text size="sm" mt="sm">{item.email}</Text><Text size="sm" c="dimmed">{item.phone}</Text>
                <Group justify="space-between" mt="md"><Text size="xs" c="dimmed">Box: {getBoxProgress(item)} de 3 · {BRANCH[item.preferredPickupBranch] || 'Sin sucursal'}</Text><Text size="sm" fw={700}>{formatMoney(item.amount)}</Text></Group>
              </Paper>
            ))}
          </Stack>
        ) : (
          <ScrollArea>
            <Table highlightOnHover verticalSpacing="sm" miw={1160}>
              <Table.Thead><Table.Tr><Table.Th>Suscriptor</Table.Th><Table.Th>Contacto</Table.Th><Table.Th>Estado</Table.Th><Table.Th>Sucursal</Table.Th><Table.Th>Avance box</Table.Th><Table.Th>Activación</Table.Th><Table.Th>Último pago</Table.Th><Table.Th>Próximo cobro</Table.Th><Table.Th ta="right">Monto</Table.Th></Table.Tr></Table.Thead>
              <Table.Tbody>{filtered.map((item) => (
                <Table.Tr key={item.id} onClick={() => showDetails(item)} style={{ cursor: 'pointer' }}>
                  <Table.Td><Text size="sm" fw={700}>{item.fullName}</Text><Text size="xs" c="dimmed">{item.rut}</Text></Table.Td>
                  <Table.Td><Text size="sm">{item.email}</Text><Text size="xs" c="dimmed">{item.phone}</Text></Table.Td>
                  <Table.Td><StatusBadge status={item.status} /></Table.Td><Table.Td>{BRANCH[item.preferredPickupBranch] || '—'}</Table.Td><Table.Td><Text size="sm" fw={700}>{getBoxProgress(item)} de 3</Text><Text size="xs" c="dimmed">{Number(item.boxRewardsEarned || 0)} obtenidos</Text></Table.Td><Table.Td>{formatDate(item.activatedAt)}</Table.Td><Table.Td>{formatDate(item.lastPaymentAt)}</Table.Td><Table.Td>{formatDate(item.nextPaymentAt)}</Table.Td><Table.Td ta="right" fw={700}>{formatMoney(item.amount)}</Table.Td>
                </Table.Tr>
              ))}</Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Paper>

      <Drawer opened={opened} onClose={closeDetails} position="right" size={isMobile ? '100%' : 560} title={<Group gap="xs"><IconCrown size={20} /><Text fw={800}>Ficha Premium Club</Text></Group>}>
        {selected && <Stack gap="lg">
          <Paper p="md" radius="lg" bg="gray.0"><Group justify="space-between" align="start"><Box><Title order={3}>{selected.fullName}</Title><Text c="dimmed" size="sm" mt={3}>{selected.rut}</Text></Box><StatusBadge status={selected.status} /></Group></Paper>
          <Box><Text fw={800} mb="sm">Datos personales</Text><SimpleGrid cols={2} spacing="md"><DataLine label="Correo" value={selected.email} /><DataLine label="Teléfono" value={selected.phone} /><DataLine label="Nacimiento" value={selected.birthDate} /><DataLine label="Regalo" value={GIFT[selected.welcomeGift] || selected.welcomeGift} /><DataLine label="Sucursal de retiro" value={BRANCH[selected.preferredPickupBranch] || selected.preferredPickupBranch} /></SimpleGrid></Box>
          <Divider />
          <Box><Text fw={800} mb="sm">Membresía</Text><SimpleGrid cols={2} spacing="md"><DataLine label="Estado externo" value={selected.externalStatus} /><DataLine label="Monto" value={`${formatMoney(selected.amount)} ${selected.currency || 'CLP'}`} /><DataLine label="Activación" value={formatDate(selected.activatedAt, true)} /><DataLine label="Último pago" value={formatDate(selected.lastPaymentAt, true)} /><DataLine label="Próximo cobro" value={formatDate(selected.nextPaymentAt, true)} /><DataLine label="Cancelación" value={formatDate(selected.cancelledAt, true)} /></SimpleGrid><Box mt="md"><DataLine label="ID Mercado Pago" value={selected.externalSubscriptionId} /></Box></Box>
          <Divider />
          <Box>
            <Group justify="space-between" mb="sm"><Text fw={800}>Programa de boxes</Text><Badge color="red" variant="light">{getBoxProgress(selected)} de 3 pagos</Badge></Group>
            <SimpleGrid cols={2} spacing="md" mb="md"><DataLine label="Pagos aprobados" value={Number(selected.approvedPaymentCount || 0)} /><DataLine label="Boxes obtenidos" value={Number(selected.boxRewardsEarned || 0)} /><DataLine label="Boxes entregados" value={selectedBoxRewards.filter((reward) => reward.status === 'delivered').length} /><DataLine label="Próximo box estimado" value={getEstimatedBoxDate(selected)} /></SimpleGrid>
            {boxesLoading ? <Loader size="sm" /> : selectedBoxRewards.length ? <Stack gap="xs">{selectedBoxRewards.map((reward) => (
              <Paper key={reward.id} withBorder p="sm" radius="md"><Group justify="space-between" align="center"><Box><Group gap="xs"><IconBox size={17} /><Text size="sm" fw={700}>Box #{reward.cycleNumber}</Text><Badge color={reward.status === 'delivered' ? 'green' : 'yellow'} variant="light">{reward.status === 'delivered' ? 'Entregado' : 'Por entregar'}</Badge></Group><Text size="xs" c="dimmed" mt={5}>{reward.status === 'delivered' ? `${formatDate(reward.deliveredAt, true)} · ${BRANCH[reward.deliveredBranch] || reward.deliveredBranch}` : `Disponible desde ${formatDate(reward.eligibleAt)}`}</Text></Box>{reward.status === 'eligible' && <Button size="xs" color="green" leftSection={<IconCheck size={15} />} loading={deliveringBoxId === reward.id} onClick={() => markBoxDelivered(reward)}>Marcar entregado</Button>}</Group></Paper>
            ))}</Stack> : <Text size="sm" c="dimmed">Aún no hay boxes obtenidos. El primero se crea al tercer pago aprobado.</Text>}
          </Box>
          <Divider />
          <Box><Text fw={800} mb="sm">Historial</Text>{eventsLoading ? <Loader size="sm" /> : events.length ? <Stack gap={0}>{events.map((event) => (
            <Box key={event.id} py="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}><Group justify="space-between" align="start" wrap="nowrap"><Box><Text size="sm" fw={700}>{event.eventType}</Text><Text size="xs" c="dimmed" mt={3}>{event.previousStatus || '—'} → {event.newStatus || '—'}</Text>{event.paymentId && <Text size="xs" c="dimmed">Pago {event.paymentId} · {event.paymentStatus || 'sin estado'}</Text>}</Box><Text size="xs" c="dimmed" ta="right">{formatDate(event.receivedAt, true)}</Text></Group></Box>
          ))}</Stack> : <Text size="sm" c="dimmed">Todavía no hay eventos registrados.</Text>}</Box>
          <Alert icon={<IconAlertCircle size={17} />} color="gray" variant="light">Esta vista es de solo lectura. Las cancelaciones se gestionan de forma asistida y se reflejan cuando Mercado Pago las confirma.</Alert>
        </Stack>}
      </Drawer>
    </Container>
  );
}
